import { useEffect, type RefObject } from "react";

/**
 * Zaštita za polja u koja kuca barkod skener.
 *
 * Skener je tastatura: šalje znakove, ponekad modifier-state uz njih, i Enter
 * na kraju. Ništa od toga ne smije izaći iz polja do globalnih listenera
 * (⌘K paleta, prečice), niti Enter smije aktivirati nešto drugo ako je polje
 * u međuvremenu izgubilo fokus.
 */

/** Omota onKeyDown polja tako da tipke iz njega ne stižu do listenera na document-u. */
export function isolateScannerKeys<E extends React.KeyboardEvent>(handler?: (e: E) => void) {
  return (e: E) => {
    // React sluša na root kontejneru, ispod document-a — stopPropagation ovdje
    // znači da document.addEventListener("keydown") nikad ne vidi ovu tipku.
    e.stopPropagation();
    handler?.(e);
  };
}

/**
 * Vraća fokus u skenersko polje čim padne na <body> (tap pored polja,
 * zatvoren confirm(), sakrivena tastatura). Bez toga skener kuca u prazno,
 * a njegov Enter aktivira šta god je zadnje bilo fokusirano.
 */
export function useScannerFocusGuard(inputRef: RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    inputRef.current?.focus();
    const onFocusOut = () => {
      setTimeout(() => {
        if (document.activeElement === document.body) inputRef.current?.focus();
      }, 0);
    };
    document.addEventListener("focusout", onFocusOut);
    return () => document.removeEventListener("focusout", onFocusOut);
  }, [inputRef]);
}
